import { useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  GripVertical,
  RotateCcw,
} from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
} from 'react-native-gesture-handler';
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { now } from '@/db/ids';
import { goBackOrTo } from '@/lib/nav';
import {
  DEFAULT_MENU_KEYS,
  QUICK_ACTIONS_MENU,
  parseMenuKeys,
} from '@/lib/quick-actions-menu';
import { getActiveProperty } from '@/lib/tasks';

/**
 * Mirrors `quick-toolbar.tsx` but edits the Quick Actions sheet that opens
 * from the center "+" FAB instead of the My Home toolbar row. Same UX:
 * long-press a row to drag, arrows for fine reorder, × to remove, tap an
 * available action to add it.
 */
const SOFT_LIMIT = 8;

export default function QuickActionsScreen() {
  const router = useRouter();
  const db = useSQLiteContext();
  const [propertyId, setPropertyId] = useState<string | null>(null);
  const [order, setOrder] = useState<string[]>(DEFAULT_MENU_KEYS);
  const [savedFlash, setSavedFlash] = useState(false);
  const activeIndex = useSharedValue(-1);
  const projectedIndex = useSharedValue(-1);
  const loadedRef = useRef(false);
  // Latest order kept in a ref so the unmount cleanup can flush even when
  // React state is stale by the time the effect fires.
  const orderRef = useRef<string[]>(order);
  orderRef.current = order;
  const propertyIdRef = useRef<string | null>(null);

  useEffect(() => {
    (async () => {
      const p = await getActiveProperty(db);
      if (!p) return;
      setPropertyId(p.id);
      propertyIdRef.current = p.id;
      setOrder(parseMenuKeys(p.quick_actions_keys_json));
      orderRef.current = parseMenuKeys(p.quick_actions_keys_json);
      loadedRef.current = true;
    })();
  }, [db]);

  const persistOrder = async (next: string[]) => {
    const pid = propertyIdRef.current;
    if (!pid) return;
    try {
      await db.runAsync(
        `UPDATE property SET quick_actions_keys_json = ?, updated_at = ? WHERE id = ?`,
        JSON.stringify(next),
        now(),
        pid
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1400);
    } catch (err) {
      console.warn('quick-actions save failed', err);
    }
  };

  // Save immediately on every order change once we've finished loading.
  // No debounce — clicks happen at human speed and SQLite writes are fast;
  // the prior debounced version lost saves when the user tapped Back fast.
  useEffect(() => {
    if (!loadedRef.current) return;
    void persistOrder(order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  // Belt-and-suspenders: if the screen unmounts while a save is in flight,
  // flush the latest ref synchronously so the user's last change can't be
  // dropped by a fast back-press.
  useEffect(() => {
    return () => {
      if (loadedRef.current) void persistOrder(orderRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = new Set(order);
  const remaining = QUICK_ACTIONS_MENU.filter((a) => !selected.has(a.key));

  const move = (i: number, delta: number) => {
    const next = order.slice();
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };

  const remove = (key: string) => setOrder((arr) => arr.filter((k) => k !== key));
  const add = (key: string) => setOrder((arr) => [...arr, key]);
  const reset = () => setOrder(DEFAULT_MENU_KEYS);

  return (
    <SafeAreaView style={styles.container} edges={['top', 'bottom']}>
      <View style={styles.topbar}>
        <Pressable
          onPress={() => goBackOrTo(router, '/(tabs)/settings')}
          style={({ pressed }) => [styles.back, pressed && { opacity: 0.7 }]}
          hitSlop={12}>
          <ChevronLeft size={22} color={Palette.ink} strokeWidth={2} />
        </Pressable>
        <Text style={styles.topbarTitle}>Quick Actions menu</Text>
        <Pressable onPress={reset} hitSlop={8} style={({ pressed }) => [styles.resetBtn, pressed && { opacity: 0.7 }]}>
          <RotateCcw size={16} color={Palette.inkSoft} strokeWidth={1.8} />
        </Pressable>
      </View>

      <ScrollView contentContainerStyle={{ paddingBottom: 90 }} showsVerticalScrollIndicator={false}>
        <Text style={styles.h1}>Tune your + menu</Text>
        <Text style={styles.sub}>
          Pick which actions show up when you tap the green + button.
          Long-press a row to drag, or use the arrows; tap × to remove.
        </Text>

        <Text style={styles.section}>YOUR MENU · {order.length}</Text>
        <GestureHandlerRootView style={styles.card}>
          {order.map((key, i) => {
            const a = QUICK_ACTIONS_MENU.find((x) => x.key === key);
            if (!a) return null;
            const isFirst = i === 0;
            const isLast = i === order.length - 1;
            return (
              <DraggableRow
                key={key}
                index={i}
                total={order.length}
                activeIndex={activeIndex}
                projectedIndex={projectedIndex}
                onReorder={(from, to) => {
                  setOrder((arr) => {
                    const next = arr.slice();
                    const [item] = next.splice(from, 1);
                    next.splice(to, 0, item);
                    return next;
                  });
                }}>
                <View style={[styles.row, i < order.length - 1 && styles.rowBorder]}>
                  <View style={styles.grip}>
                    <GripVertical size={16} color={Palette.inkFaint} strokeWidth={1.8} />
                  </View>
                  <View style={[styles.iconBubble, { backgroundColor: a.color }]}>
                    <a.Icon size={18} color={Palette.cream} strokeWidth={1.8} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.rowLabel}>{a.label}</Text>
                    <Text style={styles.rowSub} numberOfLines={1}>{a.sub}</Text>
                  </View>
                  <Pressable
                    onPress={() => move(i, -1)}
                    disabled={isFirst}
                    style={({ pressed }) => [styles.iconBtn, isFirst && { opacity: 0.25 }, pressed && { opacity: 0.6 }]}
                    hitSlop={6}>
                    <ArrowUp size={14} color={Palette.ink} strokeWidth={2} />
                  </Pressable>
                  <Pressable
                    onPress={() => move(i, 1)}
                    disabled={isLast}
                    style={({ pressed }) => [styles.iconBtn, isLast && { opacity: 0.25 }, pressed && { opacity: 0.6 }]}
                    hitSlop={6}>
                    <ArrowDown size={14} color={Palette.ink} strokeWidth={2} />
                  </Pressable>
                  <Pressable
                    onPress={() => remove(key)}
                    style={({ pressed }) => [styles.removeBtn, pressed && { opacity: 0.7 }]}
                    hitSlop={6}>
                    <Text style={styles.removeBtnText}>×</Text>
                  </Pressable>
                </View>
              </DraggableRow>
            );
          })}
          {order.length === 0 ? (
            <Text style={styles.empty}>
              Nothing in your menu — pick something from below.
            </Text>
          ) : null}
        </GestureHandlerRootView>

        {order.length > SOFT_LIMIT ? (
          <Text style={styles.warn}>
            {order.length} actions in the sheet — gets crowded past {SOFT_LIMIT}.
          </Text>
        ) : null}

        {remaining.length > 0 ? (
          <>
            <Text style={styles.section}>AVAILABLE</Text>
            <View style={styles.card}>
              {remaining.map((a, i) => (
                <Pressable
                  key={a.key}
                  onPress={() => add(a.key)}
                  style={({ pressed }) => [
                    styles.row,
                    i < remaining.length - 1 && styles.rowBorder,
                    pressed && { backgroundColor: '#EEF3EE' },
                  ]}>
                  <View style={[styles.iconBubble, { backgroundColor: a.color }]}>
                    <a.Icon size={18} color={Palette.cream} strokeWidth={1.8} />
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.rowLabel}>{a.label}</Text>
                    <Text style={styles.rowSub} numberOfLines={1}>{a.sub}</Text>
                  </View>
                  <Text style={styles.addLink}>+ Add</Text>
                </Pressable>
              ))}
            </View>
          </>
        ) : null}

        <View style={styles.savedRow}>
          <View style={styles.savedPill}>
            {savedFlash ? <Check size={12} color={Palette.cream} strokeWidth={2.6} /> : null}
            <Text style={styles.savedPillText}>
              {savedFlash ? 'Saved' : 'Auto-saves as you go'}
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Palette.sand },
  topbar: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: Spacing.four, paddingTop: Spacing.two, paddingBottom: Spacing.two,
  },
  back: {
    width: 36, height: 36, borderRadius: 18, borderWidth: 1,
    borderColor: Palette.line, backgroundColor: Palette.card,
    alignItems: 'center', justifyContent: 'center',
  },
  topbarTitle: { fontFamily: Fonts.sansBold, fontSize: 14, color: Palette.ink },
  resetBtn: {
    width: 36, height: 36, borderRadius: 18,
    alignItems: 'center', justifyContent: 'center',
  },
  h1: {
    marginHorizontal: Spacing.four, marginTop: Spacing.three,
    fontFamily: Fonts.serif, fontSize: 24, color: Palette.ink, letterSpacing: -0.3,
  },
  sub: {
    marginHorizontal: Spacing.four, marginTop: 4,
    fontSize: 13, lineHeight: 19, color: Palette.inkSoft, fontFamily: Fonts.sansMedium,
  },
  section: {
    marginTop: Spacing.five, marginHorizontal: 22,
    fontSize: 12, fontFamily: Fonts.sansBold, color: Palette.inkFaint,
    letterSpacing: 0.8, marginBottom: 8,
  },
  card: {
    marginHorizontal: 18, backgroundColor: Palette.card,
    borderWidth: 1, borderColor: Palette.line, borderRadius: Radius.xl,
    paddingHorizontal: 14,
  },
  row: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    paddingVertical: 11,
  },
  rowBorder: { borderBottomWidth: 1, borderBottomColor: Palette.line },
  iconBubble: {
    width: 34, height: 34, borderRadius: 11,
    alignItems: 'center', justifyContent: 'center',
  },
  rowLabel: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.ink },
  rowSub: {
    marginTop: 1, fontFamily: Fonts.sansMedium, fontSize: 12, color: Palette.inkSoft,
  },
  iconBtn: {
    width: 30, height: 30, borderRadius: 10,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderColor: Palette.line,
  },
  removeBtn: {
    width: 28, height: 28, borderRadius: 14,
    alignItems: 'center', justifyContent: 'center',
    marginLeft: 4,
  },
  removeBtnText: {
    fontFamily: Fonts.sansBold, fontSize: 18, color: Palette.clay,
    marginTop: -2,
  },
  addLink: { fontFamily: Fonts.sansBold, fontSize: 13.5, color: Palette.forest },
  empty: {
    paddingVertical: 14, fontFamily: Fonts.sansMedium, fontSize: 13,
    color: Palette.inkFaint, textAlign: 'center',
  },
  warn: {
    marginTop: 8, marginHorizontal: 22,
    fontFamily: Fonts.sansMedium, fontSize: 12.5, color: Palette.honeyDeep,
    lineHeight: 16,
  },
  savedRow: {
    marginTop: Spacing.four, alignItems: 'center',
  },
  savedPill: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999,
    backgroundColor: '#DCE7E1',
  },
  savedPillText: {
    fontFamily: Fonts.sansBold, fontSize: 12.5, color: Palette.forest,
    letterSpacing: 0.3,
  },
  grip: {
    width: 22, height: 28,
    alignItems: 'center', justifyContent: 'center',
    marginRight: -4,
  },
});

const ROW_HEIGHT = 56;
type SV = ReturnType<typeof useSharedValue<number>>;

function DraggableRow({
  index,
  total,
  activeIndex,
  projectedIndex,
  onReorder,
  children,
}: {
  index: number;
  total: number;
  activeIndex: SV;
  projectedIndex: SV;
  onReorder: (from: number, to: number) => void;
  children: React.ReactNode;
}) {
  const isDragging = useSharedValue(0);
  const offsetY = useSharedValue(0);

  const pan = Gesture.Pan()
    .activateAfterLongPress(300)
    .onStart(() => {
      isDragging.value = withTiming(1, { duration: 120 });
      activeIndex.value = index;
      projectedIndex.value = index;
    })
    .onUpdate((e) => {
      offsetY.value = e.translationY;
      const delta = Math.round(e.translationY / ROW_HEIGHT);
      projectedIndex.value = Math.max(0, Math.min(total - 1, index + delta));
    })
    .onEnd(() => {
      const target = projectedIndex.value;
      isDragging.value = withTiming(0, { duration: 160 });
      offsetY.value = withSpring(0, { mass: 0.6, damping: 14 });
      activeIndex.value = -1;
      projectedIndex.value = -1;
      if (target !== index) runOnJS(onReorder)(index, target);
    });

  const animatedStyle = useAnimatedStyle(() => {
    const active = activeIndex.value;
    const target = projectedIndex.value;
    const amDragging = active === index;
    let shiftY = 0;
    if (active !== -1 && !amDragging) {
      if (target > active && index > active && index <= target) shiftY = -ROW_HEIGHT;
      else if (target < active && index < active && index >= target) shiftY = ROW_HEIGHT;
    }
    return {
      transform: [
        { translateY: amDragging ? offsetY.value : withTiming(shiftY, { duration: 180 }) },
        { scale: 1 + isDragging.value * 0.03 },
      ],
      zIndex: isDragging.value > 0 ? 999 : 1,
      shadowColor: '#000',
      shadowOpacity: isDragging.value * 0.22,
      shadowRadius: 12,
      shadowOffset: { width: 0, height: 6 },
      elevation: isDragging.value > 0 ? 8 : 0,
      backgroundColor: isDragging.value > 0 ? Palette.card : 'transparent',
      borderRadius: 12,
    };
  });

  return (
    <GestureDetector gesture={pan}>
      <Animated.View style={animatedStyle}>{children}</Animated.View>
    </GestureDetector>
  );
}
